import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackEvent } from '../../lib/tracking';

const RyadContact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    apartmentType: 'F2',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { t } = useLanguage();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation simple
    if (!formData.name || !formData.email || !formData.phone) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      // Récupérer l'URL du backend depuis les variables d'environnement
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          apartment_type: formData.apartmentType,
          message: formData.message
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Contact envoyé avec succès:', result);
        // Facebook Pixel - Lead event (formulaire soumis avec succes)
        trackEvent('Lead', {
          content_name: 'Formulaire Contact RYAD',
          content_category: formData.apartmentType,
          currency: 'XOF',
        });
        setIsSubmitted(true);
        
        // Réinitialiser le formulaire après envoi réussi
        setFormData({
          name: '',
          email: '',
          phone: '',
          apartmentType: 'F2',
          message: ''
        });
        
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'envoi. Veuillez réessayer.');
    }
  };

  const handleWhatsAppForm = () => {
    // Facebook Pixel - Contact event (clic WhatsApp)
    trackEvent('Contact', {
      content_name: 'WhatsApp Contact Form',
      content_category: formData.apartmentType || 'unknown',
    });
    const message = encodeURIComponent(
      `Bonjour, je souhaite réserver un appartement à la Résidence RYAD en VEFA.\n\n` +
      `Nom: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Téléphone: ${formData.phone}\n` +
      `Type: ${formData.apartmentType}\n` +
      `Message: ${formData.message}`
    );
    window.open(`https://wa.me/221787172828?text=${message}`, '_blank');
  };

  const openGoogleMaps = () => {
    trackEvent('FindLocation', {
      content_name: 'Google Maps - Sacre Coeur Dakar',
    });
    window.open('https://maps.app.goo.gl/5yFLoZp2zbUJbJJo7', '_blank');
  };

  const trackContactClick = (type, detail) => {
    trackEvent('Contact', {
      content_name: `${type} - ${detail}`,
      content_category: type,
    });
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            <span className="text-amber-600">{t.contact.title}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Left: Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">
              Formulaire de contact
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Nom *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-colors"
                    placeholder="Votre nom"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-colors"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Téléphone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-colors"
                    placeholder="+221 XX XXX XX XX"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Type d'appartement</label>
                  <select
                    name="apartmentType"
                    value={formData.apartmentType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-colors"
                  >
                    <option value="F2">F2 - Idéal couples</option>
                    <option value="F3">F3 - Confort familial</option>
                    <option value="F4">F4 - Espace & élégance</option>
                    <option value="Penthouse">Penthouse - Luxe exclusif</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-colors resize-none"
                  placeholder="Vos questions ou demandes spécifiques..."
                ></textarea>
              </div>

              {isSubmitted && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
                  <strong>{t.contact.form.success}</strong>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300"
                >
                  Envoyer
                </button>
                <button
                  type="button"
                  onClick={handleWhatsAppForm}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  WhatsApp
                </button>
              </div>
            </form>
          </div>

          {/* Right: Contact Info & Company */}
          <div className="space-y-8">
            {/* Contact Cards */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">
                SOGI SUARL
              </h3>
              
              <div className="space-y-4 mb-6">
                <a
                  href={`https://wa.me/221787172828?text=${encodeURIComponent('Bonjour, je souhaite des informations sur la Résidence RYAD.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackContactClick('WhatsApp', 'Principal')}
                  className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 hover:shadow-md transition cursor-pointer"
                  data-testid="contact-whatsapp-principal"
                >
                  <div className="text-2xl mr-4">📱</div>
                  <div>
                    <p className="font-semibold text-gray-800">WhatsApp Principal</p>
                    <p className="text-green-600 font-bold">{t.common.phone_senegal}</p>
                  </div>
                </a>

                <a
                  href="https://wa.me/221787172828"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackContactClick('WhatsApp', 'Senegal')}
                  className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 hover:shadow-md transition cursor-pointer"
                  data-testid="contact-whatsapp-senegal"
                >
                  <div className="text-2xl mr-4">📞</div>
                  <div>
                    <p className="font-semibold text-gray-800">WhatsApp Sénégal</p>
                    <p className="text-green-600 font-bold">+221 78 717 28 28</p>
                  </div>
                </a>
                
                <a
                  href="mailto:gestion@sogi.sn"
                  onClick={() => trackContactClick('Email', 'gestion@sogi.sn')}
                  className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 hover:shadow-md transition cursor-pointer"
                  data-testid="contact-email"
                >
                  <div className="text-2xl mr-4">📧</div>
                  <div>
                    <p className="font-semibold text-gray-800">Email</p>
                    <p className="text-blue-600 font-bold">gestion@sogi.sn</p>
                  </div>
                </a>
                
                <a
                  href="tel:+221338677389"
                  onClick={() => trackContactClick('Phone', 'Fixe')}
                  className="flex items-center p-4 bg-amber-50 rounded-lg hover:bg-amber-100 hover:shadow-md transition cursor-pointer"
                  data-testid="contact-phone-fixe"
                >
                  <div className="text-2xl mr-4">🏢</div>
                  <div>
                    <p className="font-semibold text-gray-800">Fixe</p>
                    <p className="text-amber-600 font-bold">+221 33 867 73 89</p>
                  </div>
                </a>
              </div>

              <button
                onClick={openGoogleMaps}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                Localisation Sacré-Cœur
              </button>
            </div>

            {/* Horaires */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h4 className="text-xl font-bold text-gray-800 mb-4">
                🕒 Horaires
              </h4>
              <div className="space-y-2 text-gray-600">
                <div className="flex justify-between">
                  <span>Lundi - Vendredi</span>
                  <span className="font-semibold">8h - 18h</span>
                </div>
                <div className="flex justify-between">
                  <span>Samedi</span>
                  <span className="font-semibold">8h - 13h</span>
                </div>
                <div className="flex justify-between">
                  <span>Dimanche</span>
                  <span className="text-red-500 font-semibold">Fermé</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-20 pt-8 border-t border-gray-200 text-center text-gray-600">
          <p>&copy; 2025 SOGI SUARL - Résidence RYAD | Tous droits réservés</p>
        </footer>
      </div>
    </section>
  );
};

export default RyadContact;